// Die Docker-Container aus dem Homelab-Protokoll (Topics `docker.stand` und
// `docker.lage`; docs/protokoll.md › „Docker über das Protokoll“) in genau die
// Form, die die Seiten aus der REST-Antwort `GET /v2-api/docker/containers`
// kennen (`DockerHost` mit `DockerContainer`).
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/docker-protokoll.test.mjs) sie laden kann. Die Stände kommen skaliert an
// (`skaliere("docker.stand", …)`: Prozent mit zwei Stellen, die GPU mit einer);
// Gegenstück der Rechnung ist internal/protokoll/adapter/docker.go.
//
// Regel, mit der die REST-Antwort exakt wiederkehrt (der Adapter sendet
// spiegelbildlich):
//   - `name`, `image`, `status`, `state` und `ports` führt die REST-Antwort ohne
//     `omitempty`: fehlt der Text im Stand, ist er "";
//   - `stack` ist `null`, wenn der Container keinen hat; `stats` ist `null`,
//     wenn docker nichts gemeldet hat (alle vier Werte fehlen), sonst stehen
//     darin nur die Werte, die der Stand trägt -- auch als 0 (omitempty der
//     Zeiger), nie als `undefined`;
//   - `gpu` steht nur, wenn der Stand sie trägt; darin ist `prozent` immer da,
//     `speicher` nur, wenn es nicht 0 ist (Go: omitempty), `motoren` nur, wenn
//     welche da sind.
//   - Die Rechner und ihre Reihenfolge stehen in `docker.lage`; die Container
//     eines Rechners ordnet `position` (Reihenfolge von `docker ps`).

import type { DockerContainer, DockerGPU, DockerHost, DockerStats } from "./types";
import type { DockerRechner, DockerStand } from "./protokoll/schema.gen";

/** Instanzname → Id des Containers: „docker.ab12cd34ef56ab78.stand“ → „ab12cd34ef56ab78“.
 *  `docker.lage` und alles andere → undefined. */
export function dockerIdAusInstanz(instanz: string): string | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== "docker" || t[2] !== "stand" || !/^[0-9a-f]+$/.test(t[1])) return undefined;
  return t[1];
}

/** Ein Container mit dem Rechner und der Stelle, an der er steht. */
export interface DockerEintrag {
  rechner: string;
  position: number;
  container: DockerContainer;
}

/** Schlüssel eines Containers in der Liste: Rechner und Name (gleicher Name auf
 *  zwei Rechnern sind zwei Container). */
export function dockerSchluessel(rechner: string, name: string): string {
  return rechner + "\u0000" + name;
}

/** Position, die ein Container ohne bekannte Stelle bekommt (ans Ende). */
const POSITION_UNBEKANNT = 1_000_000;

function gpuAusProtokoll(g: NonNullable<DockerStand["gpu"]>): DockerGPU {
  const o: DockerGPU = { prozent: g.prozent ?? 0 };
  if (g.speicher) o.speicher = g.speicher;
  const motoren: Record<string, number> = {};
  for (const m of g.motoren ?? []) if (m.name) motoren[m.name] = m.prozent ?? 0;
  if (Object.keys(motoren).length > 0) o.motoren = motoren;
  return o;
}

/** Der Container aus seinem Stand, wie `GET /v2-api/docker/containers` ihn führt. */
export function containerAusProtokoll(st: DockerStand): DockerContainer {
  const stats: DockerStats = {};
  if (st.cpu != null) stats.cpu = st.cpu;
  if (st.speicher != null) stats.speicher = st.speicher;
  if (st.speicher_grenze != null) stats.speicher_grenze = st.speicher_grenze;
  if (st.speicher_anteil != null) stats.speicher_anteil = st.speicher_anteil;
  const o: DockerContainer = {
    name: st.name ?? "",
    image: st.image ?? "",
    status: st.status ?? "",
    state: st.zustand ?? "",
    ports: st.ports ?? "",
    stack: st.stack ?? null,
    stats: Object.keys(stats).length > 0 ? stats : null,
  };
  if (st.gpu != null) o.gpu = gpuAusProtokoll(st.gpu);
  return o;
}

/** Der Eintrag eines Containers aus seinem Stand. Ohne Rechner oder Name → undefined. */
export function dockerEintragAus(st: DockerStand): DockerEintrag | undefined {
  if (!st.rechner || !st.name) return undefined;
  return { rechner: st.rechner, position: st.position ?? POSITION_UNBEKANNT, container: containerAusProtokoll(st) };
}

/** Die Einträge einer REST-Antwort: je Container sein Rechner und sein Index. */
export function dockerEintraegeAusHosts(hosts: readonly DockerHost[]): DockerEintrag[] {
  const out: DockerEintrag[] = [];
  for (const h of hosts) h.containers.forEach((c, i) => out.push({ rechner: h.host_id, position: i, container: c }));
  return out;
}

/** Die Rechner einer REST-Antwort als Lage (Reihenfolge, Name, erreichbar). */
export function dockerLageAusHosts(hosts: readonly DockerHost[]): DockerRechner[] {
  return hosts.map((h) => ({ id: h.host_id, name: h.host_name, online: h.online }));
}

/** Die Reihenfolge der Einträge: Rechner, Stelle, Name. */
export function vergleicheDockerEintrag(a: DockerEintrag, b: DockerEintrag): number {
  if (a.rechner !== b.rechner) return a.rechner < b.rechner ? -1 : 1;
  if (a.position !== b.position) return a.position < b.position ? -1 : 1;
  return a.container.name === b.container.name ? 0 : a.container.name < b.container.name ? -1 : 1;
}

/** Die Rechner mit ihren Containern, wie die REST-Antwort sie führt: in der
 *  Reihenfolge der Lage, die Container eines Rechners nach `position` (bei
 *  gleicher Stelle nach Name). Container eines Rechners, den die Lage nicht
 *  kennt, fehlen; ein Rechner ohne Container hat `containers: []`. */
export function dockerHostsAus(rechner: readonly DockerRechner[], eintraege: readonly DockerEintrag[]): DockerHost[] {
  const je = new Map<string, DockerEintrag[]>();
  for (const e of eintraege) {
    const l = je.get(e.rechner);
    if (l) l.push(e);
    else je.set(e.rechner, [e]);
  }
  const hosts: DockerHost[] = [];
  for (const r of rechner) {
    if (!r.id) continue;
    const mit = [...(je.get(r.id) ?? [])].sort(vergleicheDockerEintrag);
    hosts.push({ host_id: r.id, host_name: r.name ?? r.id, online: r.online ?? false, containers: mit.map((e) => e.container) });
  }
  return hosts;
}

/** Tiefer Vergleich der Rechner/Container, ohne Svelte: gleiche Objekte (Identität) oder gleiche Felder. */
function gleichHost(a: DockerHost, b: DockerHost): boolean {
  return (
    a.host_id === b.host_id &&
    a.host_name === b.host_name &&
    a.online === b.online &&
    a.containers.length === b.containers.length &&
    a.containers.every((c, i) => c === b.containers[i])
  );
}

/** Die neue Liste, wobei unveränderte Rechner (gleiche Felder, dieselben
 *  Container-Objekte) das Objekt der alten behalten, und die ganze Liste die
 *  alte ist, wenn sich nichts geändert hat -- ein Stand mit neuer CPU-Last
 *  zieht sonst jede Zeile neu. */
export function dockerHostsStabil(next: DockerHost[], vorher: DockerHost[] | undefined): DockerHost[] {
  if (!vorher) return next;
  const alte = new Map(vorher.map((h) => [h.host_id, h]));
  const out = next.map((h) => {
    const alt = alte.get(h.host_id);
    return alt && gleichHost(alt, h) ? alt : h;
  });
  return vorher.length === out.length && out.every((h, i) => h === vorher[i]) ? vorher : out;
}
